import { memo, useEffect, useRef } from "react";
import { ECharts } from "echarts";
import * as echarts from "echarts";
import moment from "moment";
import SeriesColorUtils from "../../../../../../util/SeriesColorUtils";
import StringUtils from "../../../../../../util/StringUtils";
import EventManager from "../../common/event-manager/EventManager";
import { ChartWrapper, DeviceMetricsWrapper, EChartsWrapper } from "./DeviceMetrics.styled";

interface DeviceMetricProps {
    eventManager: EventManager,
    property: any,
}

const DeviceMetrics: React.FC<DeviceMetricProps> = (props)=> {
    // ref
    let echartsDomsRef = useRef<HTMLElement>(null);
    let echartsRef =useRef<ECharts>(null);

    // functions
    let onRealtimeDeviceMetricsSeriesUpdated = async (deviceMetricsSeriesList: any[])=> {
        let property = props.property;
        let seriesDatas = [];
        let deviceMetricsSeries = deviceMetricsSeriesList.find(deviceMetricsSeries => deviceMetricsSeries.property.key == property.key);
        if (deviceMetricsSeries == null) {
            return;
        } else {
            // series data
            let seriesData = [];
            let metrics = deviceMetricsSeries.metrics;
            let lastMetricTimestamp: number = null;
            let paddingGapMillis = 5 * 10000;
            for (let i = 0; i < metrics.length; i++) {
                let metric = metrics[i];
                
                // filter error metrics (mainly caused by sensor errors)
                let errorMetric = false;
                if (property.min != null && Number(metric.value) < property.min) {
                    errorMetric = true;
                } else if (property.max != null && Number(metric.value) > property.max) {
                    errorMetric = true;
                }
                if (errorMetric) {
                    if (seriesData[seriesData.length-1] != null) {
                        seriesData.push(null);
                    }
                    continue;
                }
                
                // pad data point
                if (lastMetricTimestamp !=null && Math.abs(metric.timestamp - lastMetricTimestamp) > paddingGapMillis) {
                    // down data point
                    // let downDataPoint = {name: '' + (Number(lastMetricTimestamp)+1), value: [(Number(lastMetricTimestamp)+1), null]};
                    // seriesData.push(downDataPoint);

                    // up data point
                    // let upDataPoint: any = {name: '' + (Number(metric.timestamp)-1), value: [(Number(metric.timestamp)-1), null]};
                    // seriesData.push(upDataPoint);
                    seriesData.push(null);
                }
                lastMetricTimestamp = metric.timestamp;

                // add to series data
                seriesData.push({
                    name: metric.timestamp + '',
                    value: [Number(metric.timestamp), Number(metric.value)],
                });
            }

            // seriesDatas
            seriesDatas.push(seriesData);
        }

        // min/max timestam
        let minTime: number;
        let maxTime: number;
        for(let i=0; i<seriesDatas.length; i++) {
            let seriesData = seriesDatas[i];
            if (seriesData.length <= 0) {
                continue;
            }
            let minTimestamp = seriesData[0].value[0];
            let maxTimestamp = seriesData[seriesData.length-1].value[0];
            minTime = minTime == null ? minTimestamp : Math.min(minTime, minTimestamp);
            maxTime = maxTime == null ? maxTimestamp : Math.max(maxTime, maxTimestamp);
        }

        if (echartsRef.current != null) {
            echartsRef.current.setOption({
                xAxis: {
                    min: minTime,
                    max: maxTime,
                },
                series: [{
                    data: seriesDatas[0]
                }]
            });
        }
    };

    let formatTimestampAxis = (value)=> {
        let text = moment(value).format('HH:mm:ss');
        return text;
    }

    // initialize ECharts wrapper dom
    let initializeWrapperDom = (dom)=> {
        if (dom == null) {
            return;
        }
        resizeEcharts();
    }

    // init ECharts
    let initECharts = (dom: HTMLElement)=> {
        if (dom == null) {
            // element unmount event
            let echarts = echartsRef.current;
            echarts?.dispose();
            echartsRef.current = null;
            return;
        }
        if (echarts.getInstanceByDom(dom)) {
            return;
        }

        // save to reference
        echartsDomsRef.current=dom;

        // init echarts
        let chart = echarts.init(dom);
        echartsRef.current=chart;

        // series
        let property = props.property

        // init options
        let option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                position: function (pt) {
                    return [pt[0], '10%'];
                },
                backgroundColor: "#EFEFEF",
                confine: true,
                formatter: function(params) {
                    if (params == null) {
                        return '';
                    }

                    let seriesContent = '';
                    for(let param of params) {
                        if (param.value==null) {
                            continue;
                        }

                        let errorText = '';
                        if (property.min != null && Number(param.value[1]) < property.min) {
                            errorText = '（故障）';
                        } else if (property.max != null && Number(param.value[1]) > property.max) {
                            errorText = '（故障）';
                        }
                        
                        seriesContent += `
                            <div style="">
                                <div style="padding: 5px 0px;">
                                    <span style="display: inline-block; width: 50px;">测量值:</span> 
                                    <span style="display: inline-block">${param.value[1]} ${StringUtils.nonull(property.unit)} ${errorText}</span>
                                </div>
                                <div>
                                    <span style="display: inline-block; width: 50px;">时间:</span> 
                                    <span style="display: inline-block">${moment(param.value[0]).format('YYYY-MM-DD HH:mm:ss')}</span>
                                </div>
                            </div>
                        `;
                        break;
                    }
                    let content=`
                        <div style="width: 230px;">
                            ${seriesContent}
                        </div>
                    `;
                    return content;
                },
            },
            grid: {
                x: 40,
                y: 60,
                x2: 10,
                y2: 20,
            },
            xAxis: {
                type: 'time',
                axisLine: {
                    show: true
                },
                axisTick: {
                    show: true,
                },
                minorTick: {
                    show: true,
                },
                minorSplitLine: {
                    show: true,
                    lineStyle: {
                        color: 'rgba(255, 255, 255, 0.1)',
                        width: 1
                    },
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: 'rgba(255, 255, 255, 0.1)',
                        width: 1
                    },
                },
                splitNumber: 3,
                axisLabel: {
                    show: true,
                    fontSize: 9,
                    formatter: function (value, index) {
                        return formatTimestampAxis(value);
                    }
                },
            },
            yAxis: {
                name: property.unit || 'N/A',
                type: 'value',
                boundaryGap: [0, '100%'],
                axisLine: {
                    show: true
                },
                axisTick: {
                    show: true,
                    inside: true,
                },
                axisLabel: {
                    show: true,
                    fontSize: 9,
                    distance: 0,
                    margin: 2,
                },
                minorTick: {
                    show: false
                },
                minorSplitLine: {
                    show: true,
                    fontSize: 9,
                    lineStyle: {
                        color: 'rgba(255, 255, 255, 0.1)',
                        width: 1
                    },
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: 'rgba(255, 255, 255, 0.1)',
                        width: 1
                    }
                },
                splitNumber: 3,
                min: property.min != null ? parseFloat(property.min) : 'dataMin',
                max: property.max != null ? parseFloat(property.max) : 'dataMax',
            },
            dataZoom: [
                {
                  type: 'inside',
                  start: 90,
                  end: 100
                }
            ],
            series: [
                {
                    name: property.name,
                    type: 'line',
                    color: SeriesColorUtils.getSeriesColor(1),
                    smooth: false,
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.4,
                    },
                    data: [],
                    shadowBlur: 20,
                }
            ],
        };

        chart.setOption(option);
        chart.group='DeviceRealtimeMetrics';
        echarts.connect('DeviceRealtimeMetrics');
    }

    let resizeEcharts = ()=> {
        echartsRef.current?.resize();
    };

    // init ECharts reference
    let initEChartsRef = useRef(initECharts);

    // effect
    useEffect(()=> {
        props.eventManager.addRealtimeDeviceMetricsSeriesUpdatedListener(onRealtimeDeviceMetricsSeriesUpdated);
        window.addEventListener('resize', resizeEcharts);

        // unload
        return ()=> {
            props.eventManager.removeRealtimeDeviceMetricsSeriesUpdatedListener(onRealtimeDeviceMetricsSeriesUpdated);
            window.removeEventListener('resize', resizeEcharts);
        }
    }, []);

    // render
    return (
        <DeviceMetricsWrapper ref={(dom)=> initializeWrapperDom(dom)}>
            <ChartWrapper>
                <EChartsDom refCallback={initEChartsRef.current} />
            </ChartWrapper>
        </DeviceMetricsWrapper>
    )
}

interface EChartsDomProps {
    refCallback: (dom: HTMLDivElement)=> void;
}

let EChartsDom: React.FC<EChartsDomProps> = memo((props)=> {
    return <EChartsWrapper ref={(dom)=> props.refCallback(dom)}>
    </EChartsWrapper>
});


export default DeviceMetrics;
